/**
 * Single source of truth for Template Studio's font-family dropdown (spec: multi-language font
 * catalog). Grouped by language/script, then by Sans/Serif/Display. See `brandFonts.ts` for the
 * self-hosted `@fontsource` imports that back every `selfHosted: true` entry here.
 */

export type FontCategory = 'Sans' | 'Serif' | 'Display';
export type FontGroupName = 'English' | 'Hindi & Marathi' | 'Tamil' | 'Malayalam';

export interface FontCatalogEntry {
  /** CSS font-family name, exactly as declared in the matching @font-face / system font. */
  name: string;
  category: FontCategory;
  /** false = Microsoft/system font that can't be freely self-hosted; renders only where installed. */
  selfHosted: boolean;
  /** false = single-weight font family; Bold must be disabled for it in the toolbar. */
  supportsBold: boolean;
  /** Short native-script label shown next to the option for Indic fonts, e.g. "हिन्दी". */
  scriptLabel?: string;
  /** Non-self-hosted fonts declare their own CSS fallback (e.g. Georgia -> Merriweather -> serif). */
  systemFallback?: string;
}

export interface FontCatalogGroup {
  group: FontGroupName;
  entries: FontCatalogEntry[];
}

const DEVANAGARI_LABEL = 'हिन्दी · मराठी';
const TAMIL_LABEL = 'தமிழ்';
const MALAYALAM_LABEL = 'മലയാളം';

/**
 * Fonts whose @fontsource package only ships a 400 (regular) weight — Bold must be disabled for
 * these. Verified directly against each package's published files, not just the spec's example
 * list: Tiro Devanagari Hindi/Marathi are single-weight too, though the spec didn't call them out.
 */
const SINGLE_WEIGHT_FONTS = new Set([
  'Impact',
  'Bebas Neue',
  'Anton',
  'Rozha One',
  'Yatra One',
  'Pavanam',
  'Chilanka',
  'Meera Inimai',
  'Tiro Devanagari Hindi',
  'Tiro Devanagari Marathi',
]);

/** Microsoft/system fonts kept for continuity but not self-hostable; CSS fallback substitutes an OFL font. */
const SYSTEM_FALLBACKS: Record<string, string> = {
  Arial: 'Arial, Helvetica, sans-serif',
  'Trebuchet MS': '"Trebuchet MS", "Public Sans", sans-serif',
  Georgia: 'Georgia, "Merriweather", serif',
  Impact: 'Impact, "Anton", sans-serif',
};

function entry(name: string, category: FontCategory, scriptLabel?: string): FontCatalogEntry {
  return {
    name,
    category,
    selfHosted: !(name in SYSTEM_FALLBACKS),
    supportsBold: !SINGLE_WEIGHT_FONTS.has(name),
    scriptLabel,
    systemFallback: SYSTEM_FALLBACKS[name],
  };
}

export const FONT_GROUPS: FontCatalogGroup[] = [
  {
    group: 'English',
    entries: [
      entry('Poppins', 'Sans'),
      entry('Public Sans', 'Sans'),
      entry('DM Sans', 'Sans'),
      entry('Roboto', 'Sans'),
      entry('Montserrat', 'Sans'),
      entry('Open Sans', 'Sans'),
      entry('Trebuchet MS', 'Sans'),
      entry('Arial', 'Sans'),
      entry('Merriweather', 'Serif'),
      entry('Playfair Display', 'Serif'),
      entry('Lora', 'Serif'),
      entry('Georgia', 'Serif'),
      entry('Oswald', 'Display'),
      entry('Bebas Neue', 'Display'),
      entry('Anton', 'Display'),
      entry('Big Shoulders Display', 'Display'),
      entry('Impact', 'Display'),
    ],
  },
  {
    group: 'Hindi & Marathi',
    entries: [
      entry('Noto Sans Devanagari', 'Sans', DEVANAGARI_LABEL),
      entry('Hind', 'Sans', DEVANAGARI_LABEL),
      entry('Mukta', 'Sans', DEVANAGARI_LABEL),
      entry('Noto Serif Devanagari', 'Serif', DEVANAGARI_LABEL),
      entry('Tiro Devanagari Hindi', 'Serif', DEVANAGARI_LABEL),
      entry('Tiro Devanagari Marathi', 'Serif', DEVANAGARI_LABEL),
      entry('Rozha One', 'Display', DEVANAGARI_LABEL),
      entry('Yatra One', 'Display', DEVANAGARI_LABEL),
      entry('Khand', 'Display', DEVANAGARI_LABEL),
      entry('Teko', 'Display', DEVANAGARI_LABEL),
      entry('Baloo 2', 'Display', DEVANAGARI_LABEL),
    ],
  },
  {
    group: 'Tamil',
    entries: [
      entry('Noto Sans Tamil', 'Sans', TAMIL_LABEL),
      entry('Hind Madurai', 'Sans', TAMIL_LABEL),
      entry('Mukta Malar', 'Sans', TAMIL_LABEL),
      entry('Catamaran', 'Sans', TAMIL_LABEL),
      entry('Noto Serif Tamil', 'Serif', TAMIL_LABEL),
      entry('Anek Tamil', 'Display', TAMIL_LABEL),
      entry('Baloo Thambi 2', 'Display', TAMIL_LABEL),
      entry('Pavanam', 'Display', TAMIL_LABEL),
    ],
  },
  {
    group: 'Malayalam',
    entries: [
      entry('Noto Sans Malayalam', 'Sans', MALAYALAM_LABEL),
      entry('Manjari', 'Sans', MALAYALAM_LABEL),
      entry('Anek Malayalam', 'Sans', MALAYALAM_LABEL),
      entry('Noto Serif Malayalam', 'Serif', MALAYALAM_LABEL),
      entry('Baloo Chettan 2', 'Display', MALAYALAM_LABEL),
      entry('Gayathri', 'Display', MALAYALAM_LABEL),
      entry('Chilanka', 'Display', MALAYALAM_LABEL),
      entry('Meera Inimai', 'Display', MALAYALAM_LABEL),
    ],
  },
];

const ALL_ENTRIES: FontCatalogEntry[] = FONT_GROUPS.flatMap((g) => g.entries);
const BY_NAME = new Map(ALL_ENTRIES.map((e) => [e.name, e]));

export const DEFAULT_FONT_FAMILY = 'Poppins';

/** Always appended (in this order) to every resolved font stack — the script-fallback chain. */
export const UNIVERSAL_FALLBACKS = ['Noto Sans Devanagari', 'Noto Sans Tamil', 'Noto Sans Malayalam', 'Poppins'];

export function isKnownFont(name: string): boolean {
  return BY_NAME.has(name);
}

export function fontSupportsBold(name: string): boolean {
  return BY_NAME.get(name)?.supportsBold ?? true;
}

function quote(name: string): string {
  return name.includes(' ') ? `"${name}"` : name;
}

/**
 * Builds the CSS font-family stack for a selected font: the selected font (or its system
 * fallback stack, for Arial/Georgia/Trebuchet MS/Impact), then the Indic fallbacks and Poppins
 * not already covered, then a generic sans-serif. The browser resolves per-glyph within this
 * list, so Hindi/Tamil/Malayalam text typed in an English-only font still renders properly.
 */
export function resolveFontStack(selected: string): string {
  const known = BY_NAME.get(selected);
  const head = known?.systemFallback ?? quote(selected);
  const headNames = new Set(
    head
      .split(',')
      .map((s) => s.trim().replace(/^["']|["']$/g, ''))
      .filter(Boolean),
  );
  const fallbacks = UNIVERSAL_FALLBACKS.filter((f) => !headNames.has(f)).map(quote);
  return [head, ...fallbacks, 'sans-serif'].join(', ');
}
